import React, { ReactNode, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import useRfiChangeStatus from 'router/subrouters/DataRoom/hooks/useRfiChangeStatus';
import useRfiSendAllReminders from 'router/subrouters/DataRoom/hooks/useRfiSendAllReminders';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import RfiStatusTag from '../../RfiStatusTag';
import {
  getHasRfiNewSubmission,
  getIsRfiCreationTypeChecklist,
  getIsRfiDisplayStatusClosed,
  getIsRfiDisplayStatusCompleted,
  getIsRfiOverdue,
} from '../utils';
import BellIcon from 'icons/Bell.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import BookmarkIcon from 'icons/Bookmark.icon';
import RefreshIcon from 'icons/Refresh.icon';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import ResolvePopup from 'components/ResolvePopup';
import Counter from 'components/Counter';
import useRfiDueDateDisplay from '../hooks/useRfiDueDateDisplay';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { getResolveUrl, getRFILinkRoute } from 'utils';

type CellProps = {
  rfi: DataRoomRFI;
  onRfiUpdate: (rfi: DataRoomRFI) => void;
};

export type RfiTableRow = Pick<
  DataRoomRFI,
  'name' | 'numberOfViews' | 'dueDate' | 'displayStatus'
> &
  Record<'completed' | 'actions', string>;

export const TABLE_COLUMNS: Array<keyof RfiTableRow> = [
  'name',
  'numberOfViews',
  'displayStatus',
  'completed',
  'dueDate',
  'actions',
];

export const HEADING_TRANSLATIONS: Record<keyof RfiTableRow, string> = {
  name: 'request',
  numberOfViews: 'views',
  displayStatus: 'status',
  completed: 'completed',
  dueDate: 'dueDate',
  actions: 'actions',
};

export const SORTING_VALUES: Partial<Record<keyof RfiTableRow, string>> = {
  name: 'name',
  numberOfViews: 'numberOfViews',
  displayStatus: 'displayStatus',
  completed: 'completedCategories',
  dueDate: 'dueDate',
};

export const CUSTOM_COL_SIZES: Partial<Record<keyof RfiTableRow, number>> = {
  name: 320,
  numberOfViews: 100,
  displayStatus: 170,
  completed: 130,
  dueDate: 180,
  actions: 140,
};

const NameCell: React.FC<CellProps> = ({ rfi }) => (
  <p className="rfi-requests-table__name">
    <span>{rfi.name}</span>
    {getHasRfiNewSubmission(rfi) && <Counter onlyDot />}
  </p>
);

const ViewsCell: React.FC<CellProps> = ({ rfi }) => (
  <p>{rfi.numberOfViews || '/'}</p>
);

const StatusCell: React.FC<CellProps> = ({ rfi }) => (
  <RfiStatusTag status={rfi.displayStatus} />
);

const CompletedCell: React.FC<CellProps> = ({ rfi }) => {
  const { completedCategories, totalCategories } = rfi;

  return (
    <p>
      {completedCategories} / {totalCategories}
    </p>
  );
};

const DueDateCell: React.FC<CellProps> = ({ rfi }) => {
  const { dueDate } = rfi;

  return (
    <span
      className={classNames('rfi-requests-table__due-date', {
        overdue: getIsRfiOverdue(rfi),
      })}
    >
      {useRfiDueDateDisplay(dueDate)}
    </span>
  );
};

const ActionsCell: React.FC<CellProps> = ({ rfi, onRfiUpdate }) => {
  const { displayStatus, creationType, externalKey } = rfi;

  const { t } = useTranslation();

  const { changeStatus } = useRfiChangeStatus(rfi, onRfiUpdate);

  const { sendAllReminders, hasRecipients } = useRfiSendAllReminders(
    rfi,
    onRfiUpdate,
  );

  const { openSaveAsTemplateModal } = useContext(RfiContext);

  const copyToClipboard = useCopyToClipboard();

  const isClosed = getIsRfiDisplayStatusClosed(displayStatus);
  const isCompleted = getIsRfiDisplayStatusCompleted(displayStatus);
  const isChecklist = getIsRfiCreationTypeChecklist(creationType);
  const isOpenAndInProgress = !isClosed && !isCompleted;
  const isClosedOrCompleted = isClosed || isCompleted;

  return (
    <div
      className="rfi-requests-table__actions"
      onClick={(e) => e.stopPropagation()}
    >
      <ResolvePopup onHover popupContent={t('copyLink')}>
        <Button
          variant="icon"
          icon={<ClipboardcopyIcon strokeWidth={1.2} />}
          aria-label={t('copyLink')}
          onClick={(e) =>
            copyToClipboard(e, getResolveUrl(getRFILinkRoute(externalKey)))
          }
        />
      </ResolvePopup>
      {isOpenAndInProgress && hasRecipients && (
        <ResolvePopup onHover popupContent={t('sendReminder')}>
          <Button
            variant="icon"
            icon={BellIcon}
            aria-label={t('sendReminder')}
            onClick={sendAllReminders}
          />
        </ResolvePopup>
      )}
      {isClosed && (
        <ResolvePopup onHover popupContent={t('reopen')}>
          <Button
            variant="icon"
            icon={RefreshIcon}
            aria-label={t('reopen')}
            onClick={changeStatus}
          />
        </ResolvePopup>
      )}
      {isClosedOrCompleted && isChecklist && (
        <ResolvePopup onHover popupContent={t('saveAsTemplate')}>
          <Button
            variant="icon"
            icon={BookmarkIcon}
            aria-label={t('saveAsTemplate')}
            onClick={() => openSaveAsTemplateModal(rfi)}
          />
        </ResolvePopup>
      )}
      {!isClosed && (
        <ResolvePopup onHover popupContent={t('close')}>
          <Button
            variant="icon"
            icon={XcircleIcon}
            aria-label={t('close')}
            onClick={changeStatus}
          />
        </ResolvePopup>
      )}
    </div>
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof RfiTableRow,
  (cellProps: CellProps) => ReactNode
> = {
  name: (cellProps) => <NameCell {...cellProps} />,
  numberOfViews: (cellProps) => <ViewsCell {...cellProps} />,
  displayStatus: (cellProps) => <StatusCell {...cellProps} />,
  completed: (cellProps) => <CompletedCell {...cellProps} />,
  dueDate: (cellProps) => <DueDateCell {...cellProps} />,
  actions: (cellProps) => <ActionsCell {...cellProps} />,
};
